import { Link } from "@tanstack/react-router";

import { MARKETING_FLAGS } from "@/lib/marketing-flags";
import { lookupBy } from "@/lib/table-lookup";
import { useCookiePreferencesStore } from "@/stores/cookie-preferences-store";
import { MarketingLogo } from "./logo";
import { SocialLinks } from "./social-links";

const HOME = "/" as const;

/**
 * Footer links to marketing pages that are built get their real route; the rest
 * fall back to the landing page until those routes land.
 */
const BUILT_ROUTES = {
  Features: "/features",
  Integrations: "/integrations",
  Pricing: "/pricing",
  "AI composer": "/features",
  Analytics: "/features",
  Changelog: "/changelog",
  About: "/about",
  "Creator Partner Program": "/partners",
  Careers: "/careers",
  Blog: "/blog",
  Contact: "/contact",
  "Bluesky scheduler": "/bluesky-scheduler",
  "Mastodon scheduler": "/mastodon-scheduler",
  "Facebook scheduler": "/facebook-scheduler",
  "Instagram scheduler": "/instagram-scheduler",
  "TikTok scheduler": "/tiktok-scheduler",
  "YouTube scheduler": "/youtube-scheduler",
  "Pinterest scheduler": "/pinterest-scheduler",
  "Threads scheduler": "/threads-scheduler",
  "X (Twitter) scheduler": "/twitter-scheduler",
  "LinkedIn scheduler": "/linkedin-scheduler",
  "All free tools": "/tools",
  "2027 content calendar": "/tools/content-calendar-2027",
  "Character counter": "/tools/character-counter",
  "Bulk CSV checker": "/tools/bulk-csv-checker",
  "Bluesky best-time tool": "/tools/bluesky-best-time",
  "Bluesky engagement rate": "/tools/bluesky-engagement-rate",
  "Mastodon limit checker": "/tools/mastodon-limits",
  "Mastodon best-time tool": "/tools/mastodon-best-time",
  "Video format checker": "/tools/video-checker",
  "Thread splitter": "/tools/thread-splitter",
  "Compare alternatives": "/alternatives",
  "Client approvals": "/client-approvals",
  "AI agents (MCP)": "/ai-agents",
  "Help center": "/help",
  "Limits by network": "/limits",
  Guides: "/blog",
  "Privacy Policy": "/privacy",
  "Terms of Service": "/terms",
  "Cookie Policy": "/cookie-policy",
  GDPR: "/gdpr",
  "Data deletion": "/data-deletion",
  "Creator Partner Terms": "/partners/terms",
} as const;

const columns = [
  {
    title: "Product",
    links: [
      "Features",
      "Integrations",
      "Pricing",
      "AI composer",
      "Analytics",
      "Client approvals",
      "AI agents (MCP)",
      "Changelog",
    ],
  },
  {
    title: "Company",
    links: ["About", "Careers", "Blog", "Contact", "Creator Partner Program"],
  },
  {
    title: "Schedule for",
    // Biggest networks first, the way people search for them.
    links: [
      "Instagram scheduler",
      "Facebook scheduler",
      "YouTube scheduler",
      "TikTok scheduler",
      "Pinterest scheduler",
      "X (Twitter) scheduler",
      "Threads scheduler",
      "Bluesky scheduler",
      "Mastodon scheduler",
      "Integrations",
    ],
  },
  {
    // The free tools carry their own column rather than sitting eight-deep in
    // Resources: they are the pages built to be found and linked, and a footer
    // link is currently the only route into them from the rest of the site.
    title: "Tools",
    links: [
      "All free tools",
      "Character counter",
      "2027 content calendar",
      "Bulk CSV checker",
      "Thread splitter",
      "Video format checker",
      "Mastodon limit checker",
      "Bluesky engagement rate",
      "Bluesky best-time tool",
      "Mastodon best-time tool",
    ],
  },
  {
    title: "Resources",
    links: [
      "Help center",
      "Guides",
      "Limits by network",
      "Compare alternatives",
    ],
  },
  {
    title: "Legal",
    links: [
      "Privacy Policy",
      "Terms of Service",
      "Cookie Policy",
      "GDPR",
      "Data deletion",
      "Creator Partner Terms",
    ],
  },
] as const;

/** Labels for pages hidden pre-launch (see `marketing-flags`). */
const hiddenLabels = new Set<string>([
  ...(MARKETING_FLAGS.blog ? [] : ["Blog", "Guides"]),
  ...(MARKETING_FLAGS.partners
    ? []
    : ["Creator Partner Program", "Creator Partner Terms"]),
  ...(MARKETING_FLAGS.careers ? [] : ["Careers"]),
  ...(MARKETING_FLAGS.changelog ? [] : ["Changelog"]),
]);

export function SiteFooter() {
  const openCookiePreferences = useCookiePreferencesStore(
    (state) => state.openCookiePreferences,
  );

  return (
    <footer className="border-border border-t bg-[oklch(0.975_0.006_264)]">
      <div className="mx-auto max-w-[1200px] px-6 pt-[66px] pb-[34px]">
        <div className="flex flex-wrap justify-between gap-x-10 gap-y-12">
          <div className="min-w-[260px] max-w-[360px] flex-[1_1_300px]">
            <MarketingLogo />
            <p className="mt-4 max-w-[300px] text-[14.5px] text-muted-foreground leading-relaxed">
              The calm way to plan, schedule and publish across your social
              networks — built to actually ship your posts.
            </p>
            <div className="mt-6">
              <div className="mb-3 font-semibold text-[11px] text-[oklch(0.45_0.03_264)] uppercase tracking-[0.08em]">
                Follow us
              </div>
              <SocialLinks />
            </div>
          </div>

          <div className="flex flex-wrap gap-x-[52px] gap-y-11">
            {columns.map((column) => {
              const links = column.links.filter(
                (label) => !hiddenLabels.has(label),
              );
              if (links.length === 0) return null;
              return (
                <div key={column.title} className="min-w-[120px]">
                  <div className="mb-4 font-semibold text-[11px] text-[oklch(0.45_0.03_264)] uppercase tracking-[0.08em]">
                    {column.title}
                  </div>
                  <div className="flex flex-col gap-[11px]">
                    {links.map((label) => (
                      <Link
                        key={label}
                        to={lookupBy(BUILT_ROUTES, label) ?? HOME}
                        className="text-[oklch(0.42_0.02_264)] text-sm no-underline transition-colors hover:text-primary"
                      >
                        {label}
                      </Link>
                    ))}
                    {/*
                      Rendered after the links rather than added to `links`,
                      because that array is a list of labels and every entry
                      becomes a `<Link>` — a label with no `BUILT_ROUTES` entry
                      would silently point at the landing page. It also has to
                      be a button, not a link: the law asks for a way to change
                      a choice, and the Cookie Policy is a page about choices,
                      not the control itself.
                    */}
                    {column.title === "Legal" && (
                      <button
                        type="button"
                        onClick={openCookiePreferences}
                        className="cursor-pointer text-left text-[oklch(0.42_0.02_264)] text-sm no-underline transition-colors hover:text-primary"
                      >
                        Cookie settings
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-[52px] flex flex-wrap items-center justify-between gap-4 border-border border-t pt-6">
          <span className="text-[13.5px] text-muted-foreground">
            © 2026 PostDaily — a product of Galaxyway AI Ltd
          </span>
        </div>
      </div>
    </footer>
  );
}
